import { createContext, useContext, useState } from 'react';
import { produce } from 'immer';
import {
  BookOpenIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import typesOfReadings from '../../../../public/json/types-of-readings.json';

// Fields specific to the input type
const PacketInputContext = createContext();
export const usePacketInput = () => useContext(PacketInputContext);

export const LITERALLY_ANYTHING_ID = 'topic';
export const BOOKS_ID = 'tradebook';
export const TEXT_OR_LINK_ID = 'text_or_link';
export const VOCABULARY_ID = 'vocabulary';

export const TABS = [
  {
    id: LITERALLY_ANYTHING_ID,
    icon: GlobeAltIcon,
    text: 'Literally Anything',
    hash: 'topic',
  },
  {
    id: BOOKS_ID,
    icon: BookOpenIcon,
    text: 'Books',
    hash: 'books',
  },
  {
    id: TEXT_OR_LINK_ID,
    icon: DocumentTextIcon,
    text: 'Text, PDFs & Links',
    hash: 'text_or_link',
  },
  {
    id: VOCABULARY_ID,
    icon: ListBulletIcon,
    text: 'Vocabulary',
    hash: 'vocabulary',
  },
];

export const getVocabWordListFromString = (str) => {
  return str.split(/\r\n|\r|\n|,/).filter((item) => item.trim() !== '');
};

const getInitialTypeOfReading = () => {
  return typesOfReadings.find((type) => type.name === 'Informational Text');
};

// Input states specific different packet types
export const PacketInputProvider = ({ children }) => {
  const [inputType, _setInputType] = useState(TABS[0]);

  const [anythingInput, setAnythingInput] = useState({
    topic: '',
    genre: getInitialTypeOfReading(),
  });

  const [bookInput, setBookInput] = useState({
    book: null,
    startChapter: -1,
    endChapter: -1,
  });

  const [preserveOriginalText, setPreserveOriginalText] = useState(false);

  const [textOrLinkInput, setTextOrLinkInput] = useState({
    text: '',
    url: '',
    pdfFile: null,
    uploadedPdfURL: null,
  });

  const [vocabularyInput, setVocabularyInput] = useState({
    words: '',
  });

  const selectors = {
    // input type (the tab that is currently selected)
    inputType,
    getIsActiveTab: (tab) => {
      return inputType.id === tab.id;
    },

    // anything input
    topic: anythingInput.topic,
    genre: anythingInput.genre,

    // text or link input
    text: textOrLinkInput.text,
    url: textOrLinkInput.url,
    pdfFile: textOrLinkInput.pdfFile,
    uploadedPdfURL: textOrLinkInput.uploadedPdfURL,

    // books input
    book: bookInput.book,
    startChapter: bookInput.startChapter,
    endChapter: bookInput.endChapter,

    // vocabulary input
    words: vocabularyInput.words,

    // preserve original text
    preserveOriginalText,
  };

  const mutators = {
    // immer produce functions
    setTopic: (topic) => {
      setAnythingInput((prev) =>
        produce(prev, (draft) => {
          draft.topic = topic;
        })
      );
    },
    setGenre: (genre) => {
      setAnythingInput((prev) =>
        produce(prev, (draft) => {
          draft.genre = genre;
        })
      );
    },
    setBook: (book) => {
      setBookInput((prev) =>
        produce(prev, (draft) => {
          draft.book = book;
        })
      );
    },
    setStartChapter: (startChapter) => {
      setBookInput((prev) =>
        produce(prev, (draft) => {
          draft.startChapter = startChapter;
        })
      );
    },
    setEndChapter: (endChapter) => {
      setBookInput((prev) =>
        produce(prev, (draft) => {
          draft.endChapter = endChapter;
        })
      );
    },
    setText: (text) => {
      setTextOrLinkInput((prev) =>
        produce(prev, (draft) => {
          draft.text = text;
          draft.url = ''; // clear url when text is set
          draft.pdfFile = null; // clear pdf file when text is set
          draft.uploadedPdfURL = null; // clear uploaded pdf url when text is set
        })
      );
    },
    setUrl: (url) => {
      setTextOrLinkInput((prev) =>
        produce(prev, (draft) => {
          draft.url = url;
          draft.text = ''; // clear text when url is set
          draft.pdfFile = null; // clear pdf file when url is set
          draft.uploadedPdfURL = null; // clear uploaded pdf url when url is set
        })
      );
    },
    setPdfFile: (pdfFile) => {
      setTextOrLinkInput((prev) =>
        produce(prev, (draft) => {
          draft.pdfFile = pdfFile;
          draft.text = ''; // clear text when pdf is set
          draft.url = ''; // clear url when pdf is set
          draft.uploadedPdfURL = null; // clear uploaded pdf url when pdf is reset
        })
      );
    },
    setUploadedPdfURL: (url) => {
      setTextOrLinkInput((prev) =>
        produce(prev, (draft) => {
          draft.uploadedPdfURL = url;
        })
      );
    },
    setWords: (words) => {
      setVocabularyInput((prev) =>
        produce(prev, (draft) => {
          draft.words = words;
        })
      );
    },
    setInputType: (inputType) => {
      _setInputType(() => inputType);
    },
    setPreserveOriginalText: (preserveOriginalText) => {
      setPreserveOriginalText(preserveOriginalText);
    },
  };

  const getInputValues = () => {
    return {
      ...selectors,
    };
  };

  const value = {
    ...selectors,
    ...mutators,
    getInputValues,
  };

  return <PacketInputContext.Provider value={value}>{children}</PacketInputContext.Provider>;
};
